import React, { useState, useCallback, useEffect } from 'react';
import FlowList from './components/FlowList';
import FlowDesigner from './components/FlowDesigner';
import { SavedWorkflow } from './types/workflow';
import { getAllWorkflows, getWorkflowById } from './services/workflowService';
import './styles/App.css';

type View = { type: 'list' } | { type: 'designer'; workflowId?: string };

const App: React.FC = () => {
    const [view, setView] = useState<View>({ type: 'list' });
    const [workflows, setWorkflows] = useState<SavedWorkflow[]>([]);
    const [currentWorkflow, setCurrentWorkflow] = useState<SavedWorkflow | undefined>(undefined);
    const [loading, setLoading] = useState(true);

    const refreshWorkflows = useCallback(async () => {
        try {
            setLoading(true);
            const data = await getAllWorkflows();
            setWorkflows(data);
        } catch (err) {
            console.error('Failed to load workflows:', err);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        refreshWorkflows();
    }, [refreshWorkflows]);

    const handleCreateNew = () => {
        setCurrentWorkflow(undefined);
        setView({ type: 'designer' });
    };

    const handleEdit = async (id: string) => {
        try {
            setLoading(true);
            const workflow = await getWorkflowById(id);
            setCurrentWorkflow(workflow);
            setView({ type: 'designer', workflowId: id });
        } catch (err) {
            console.error('Failed to load workflow:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleBack = () => {
        refreshWorkflows();
        setCurrentWorkflow(undefined);
        setView({ type: 'list' });
    };

    const handleSaved = () => {
        refreshWorkflows();
        setCurrentWorkflow(undefined);
        setView({ type: 'list' });
    };

    if (loading && view.type === 'list') {
        return (
            <div className="loading-screen">
                <div className="loading-spinner"></div>
                <p>Loading workflows...</p>
            </div>
        );
    }

    if (view.type === 'designer') {
        return <FlowDesigner workflow={currentWorkflow} onBack={handleBack} onSaved={handleSaved} />;
    }

    return (
        <FlowList
            workflows={workflows}
            onCreateNew={handleCreateNew}
            onEdit={handleEdit}
            onRefresh={refreshWorkflows}
        />
    );
};

export default App;